import React from "react";
import { X, Plus, Clock4, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function to12h(hhmm) {
  if (!hhmm) return "";
  const [hStr, mStr] = String(hhmm).split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr || "0", 10);
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}
function getTimezoneLabel() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time";
  } catch {
    return "Local time";
  }
}

const DAYS = [
  { code: "Mon", label: "Mon" },
  { code: "Tue", label: "Tue" },
  { code: "Wed", label: "Wed" },
  { code: "Thu", label: "Thu" },
  { code: "Fri", label: "Fri" },
  { code: "Sat", label: "Sat" },
  { code: "Sun", label: "Sun" },
];

export default function DayTimePicker({
  days = [],
  times = [],
  onChangeDays,
  onChangeTimes,
}) {
  const toggleDay = (code) => {
    const next = days.includes(code) ? days.filter((d) => d !== code) : [...days, code];
    // Keep ordered Mon..Sun
    const order = DAYS.map((d) => d.code);
    next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    onChangeDays?.(next);
  };
  const updateTime = (i, value) => {
    const next = [...times];
    next[i] = value;
    onChangeTimes?.(next);
  };
  const addTime = () => onChangeTimes?.([...times, "10:00"]);
  const removeTime = (i) => onChangeTimes?.(times.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-[color:var(--text-2)] mb-2 flex items-center gap-2">
          <span>Posting days</span>
          <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">choose any</span>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="day-picker">
          {DAYS.map((d) => {
            const active = days.includes(d.code);
            return (
              <button
                key={d.code}
                type="button"
                onClick={() => toggleDay(d.code)}
                data-testid={`day-${d.code}`}
                className={
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-all " +
                  (active
                    ? "chip-grad text-[color:var(--text-1)]"
                    : "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                }
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-xs text-[color:var(--text-2)] mb-2 flex items-center gap-2 flex-wrap">
          <Clock4 className="h-3 w-3" />
          <span>Posting times</span>
          <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">12-hour preview</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] normal-case text-[color:var(--text-3)]">
            <Globe className="h-3 w-3" /> {getTimezoneLabel()}
          </span>
        </div>
        <div className="space-y-2" data-testid="time-picker">
          {times.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                type="time"
                value={t}
                onChange={(e) => updateTime(i, e.target.value)}
                data-testid={`time-${i}`}
                className="max-w-[140px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
              />
              <span
                className="text-xs font-mono text-[color:var(--text-2)] min-w-[64px]"
                data-testid={`time-${i}-12h`}
              >
                {to12h(t)}
              </span>
              {times.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removeTime(i)}
                  data-testid={`time-remove-${i}`}
                  className="h-9 w-9 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-3)] hover:text-[color:var(--danger)] flex items-center justify-center"
                  aria-label="Remove time"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={addTime}
            data-testid="time-add"
            className="border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
            size="sm"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add another time
          </Button>
        </div>
      </div>
      <div className="text-xs text-[color:var(--text-3)]">
        Posts per week: <span className="text-[color:var(--text-1)] font-mono-tabular">{days.length * times.length}</span>
      </div>
    </div>
  );
}
